import React, { useEffect, useState } from 'react';
import { Modal, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import axios from '../../../../../services/AxiosInstance';
import swal from 'sweetalert';
import { getApiUrl } from '../../../../../utils/ApiUtils';
import { useFormik, FormikProvider } from 'formik';
import * as Yup from 'yup';

// Lucide React Icons
import {
  Building,
  Search,
  MapPin,
  Phone,
  Hash,
  Globe,
  Mail,
  CheckCircle,
  AlertCircle,
  Info,
  X
} from 'lucide-react';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Företagsnamn är obligatoriskt'),
  orgnumber: Yup.string().required('Org-nummer är obligatoriskt'),
  vatnumber: Yup.string().nullable(),
  address: Yup.string().required('Adress är obligatoriskt'),
  city: Yup.string().required('Stad är obligatorisk'),
  postalcode: Yup.string().required('Postnummer är obligatoriskt'),
  country: Yup.string().required('Land är obligatoriskt'),
  phonenumber: Yup.string().nullable()
});

// Default empty form values
const emptyFormValues = {
  supplier_id: '',
  orgnumber: '',
  vatnumber: '',
  name: '',
  address: '',
  city: '',
  postalcode: '',
  country: '',
  phonenumber: ''
};

const NewSupplierModal = ({ show, handleClose, handleSave, handleUpdate, initialSupplierData }) => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Configure Formik with enableReinitialize to reset when initialValues change
  const formik = useFormik({
    initialValues: emptyFormValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        // Distinguish between editing (has supplier_id) and creating (no supplier_id, even if OCR-prefilled)
        if (initialSupplierData?.supplier_id) {
          await handleUpdate(values);
        } else {
          await handleSave(values);
        }
        handleModalClose();
      } catch (error) {
        console.error('Error submitting form:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  const { values, errors, touched, setFieldValue, handleChange, handleSubmit, resetForm } = formik;

  // Set initial values when modal opens or initialSupplierData changes
  useEffect(() => {
    if (show) {
      if (initialSupplierData) {
        // Editing mode or OCR-prefill - populate form with supplier data
        resetForm({
          values: {
            supplier_id: initialSupplierData.supplier_id || '',
            orgnumber: initialSupplierData.orgnumber || '',
            vatnumber: initialSupplierData.vatnumber || '',
            name: initialSupplierData.name || '',
            address: initialSupplierData.postadress || initialSupplierData.postadress1 || initialSupplierData.address || '',
            city: initialSupplierData.city || '',
            postalcode: initialSupplierData.postnumber || initialSupplierData.postalcode || '',
            country: initialSupplierData.country || '',
            phonenumber: initialSupplierData.phone || initialSupplierData.phonenumber || ''
          }
        });
      } else {
        // Creation mode - reset to empty form
        resetForm({
          values: { ...emptyFormValues }
        });
      }

      // Reset the companies search results and selected company
      setCompanies([]);
      setSelectedCompany(null);
    }
  }, [show, initialSupplierData, resetForm]);

  const searchCompany = async (inputValue) => {
    if (!inputValue || inputValue.trim().length < 3) {
      setSearchError('Ange minst 3 tecken för att söka');
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setCompanies([]);

    try {
      const searchCompanyApiUrl = getApiUrl('generic/search-company');
      const response = await axios.get(searchCompanyApiUrl, { params: { query: inputValue } });

      if (response.data.hits.length === 0) {
        setSearchError('Inga företag hittades för denna sökning');
        return;
      }

      setCompanies(
        response.data.hits.map((company) => ({
          label: `${company.document.names[0].nameOrIdentifier} (${company.document.registrationNumber})`,
          value: company,
        }))
      );
      setSearchError(null);
    } catch (err) {
      setSearchError('Kunde inte hämta företagsinformation. Försök igen senare eller ange informationen manuellt.');
      console.error('Company search error:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleCompanySelect = (selectedOption) => {
    if (!selectedOption) {
      setSelectedCompany(null);
      return;
    }

    const company = selectedOption.value;
    setSelectedCompany(company);

    const selectedAddress = company.document.mostRecentMailingAddress ||
      company.document.mostRecentVisitingAddress ||
      company.document.mostRecentRegisteredAddress;

    const phoneNumber = company.document.phoneNumbers?.length > 0 ? company.document.phoneNumbers[0].e164PhoneNumber : '';

    // Find the name with companyNameingType legalName
    const nameObject = company.document.names.find(name => name.companyNamingType === 'legalName');
    const name = nameObject ? nameObject.nameOrIdentifier : '';

    setFieldValue('orgnumber', company.document.registrationNumber || '');
    setFieldValue('vatnumber', company.document.isRegisteredForVAT ? `SE${company.document.registrationNumber}01` : '');
    setFieldValue('name', name);
    setFieldValue('address', selectedAddress?.streetAddress || '');
    setFieldValue('city', selectedAddress?.city || '');
    setFieldValue('postalcode', selectedAddress?.postalCode || '');
    setFieldValue('country', company.document.iso3166CountryCode || 'SE');
    setFieldValue('phonenumber', phoneNumber);
  };

  const handleModalClose = () => {
    // Reset form and state
    resetForm();
    setCompanies([]);
    setSelectedCompany(null);
    setSearchError(null);
    setIsSearching(false);
    setIsSubmitting(false);
    handleClose();
  };

  return (
    <Modal 
      size='lg' 
      show={show} 
      onHide={handleModalClose}
      centered
      backdrop="static"
    >
      <style>{`
        .modern-modal .modal-content {
          border: none;
          border-radius: 16px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        }

        .modern-input {
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          padding: 0.75rem;
          transition: all 0.2s ease;
          font-size: 0.875rem;
        }

        .modern-input:focus {
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 3px var(--rgba-primary-1);
          outline: none;
        }

        .modern-input.error {
          border-color: #dc2626;
        }

        .field-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #6b7280;
          pointer-events: none;
        }

        .field-wrapper {
          position: relative;
        }

        .field-wrapper input {
          padding-left: 40px;
        }

        .validation-message {
          display: flex;
          align-items: center;
          margin-top: 0.5rem;
          font-size: 0.75rem;
        }

        .search-section {
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .company-result {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 0.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .company-result:hover {
          border-color: var(--bs-primary);
          box-shadow: 0 2px 8px var(--rgba-primary-1);
        }

        .modern-modal .section-header {
          display: flex;
          align-items: center;
          margin-bottom: 1rem;
          padding-bottom: 0;
          background: none;
          border: none;
          border-radius: 0;
          padding: 0;
        }

        .modern-modal .section-icon {
          color: #9ca3af;
          margin-right: 0.5rem;
          display: flex;
          align-items: center;
        }

        .modern-modal .section-title {
          font-size: 0.75rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .coming-soon-badge {
          display: inline-flex;
          align-items: center;
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          color: #92400e;
          font-size: 0.625rem;
          font-weight: 600;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-left: 0.75rem;
        }

        .search-section.disabled {
          opacity: 0.6;
          pointer-events: none;
          position: relative;
        }

        .search-section.disabled::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(248, 250, 252, 0.5);
          border-radius: 12px;
        }
      `}</style>

      <div className="modern-modal">
        <FormikProvider value={formik}>
          <form onSubmit={handleSubmit}>
            <Modal.Header style={{ borderBottom: '1px solid #e5e7eb', padding: '1.5rem' }}>
              <div className="d-flex align-items-center">
                <div 
                  className="me-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: 'var(--bs-primary)',
                    color: 'white'
                  }}
                >
                  <Building size={20} />
                </div>
                <div>
                  <Modal.Title style={{ margin: 0, fontWeight: '600' }}>
                    {initialSupplierData?.supplier_id ? 'Redigera leverantör' : 'Ny leverantör'}
                  </Modal.Title>
                  <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                    {initialSupplierData?.supplier_id ? 'Uppdatera leverantörsinformation' : 'Lägg till en ny leverantör i systemet'}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline-secondary" 
                onClick={handleModalClose}
                style={{ 
                  border: 'none', 
                  background: '#f3f4f6',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={16} />
              </Button>
            </Modal.Header>

            <Modal.Body style={{ padding: '2rem' }}>
              {/* Company Search Section - Coming Soon */}
              {!initialSupplierData?.supplier_id && (
                <div className="search-section disabled">
                  <div className="d-flex align-items-center mb-3">
                    <Search size={14} style={{ color: '#9ca3af', marginRight: '0.5rem' }} />
                    <span className="section-title">Automatisk företagssökning</span>
                    <span className="coming-soon-badge">Kommer snart</span>
                  </div>

                  <Row>
                    <Col md={8}>
                      <div className="field-wrapper">
                        <Building className="field-icon" size={16} />
                        <input
                          type="text"
                          className="modern-input w-100"
                          placeholder="Sök efter företag..."
                          disabled
                          style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                        />
                      </div>
                    </Col>
                    <Col md={4}>
                      <Button
                        variant="outline-secondary"
                        disabled
                        style={{
                          padding: '0.75rem 1rem',
                          width: '100%',
                          fontWeight: '500',
                          cursor: 'not-allowed'
                        }}
                      >
                        <Search size={16} className="me-2" />
                        Sök företag
                      </Button>
                    </Col>
                  </Row>

                  <p style={{ margin: '0.75rem 0 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>
                    Automatisk ifyllning av företagsuppgifter kommer snart. Fyll i uppgifterna manuellt nedan.
                  </p>
                </div>
              )}

              {/* Company Information Section */}
              <div className="mb-4">
                <div className="section-header">
                  <div className="section-icon">
                    <Building size={14} />
                  </div>
                  <span className="section-title">Företagsinformation</span>
                </div>

                <div className="mb-3">
                  <label style={{ fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                    Företagsnamn *
                  </label>
                  <div className="field-wrapper">
                    <Building className="field-icon" size={16} />
                    <input
                      type="text"
                      className={`modern-input w-100 ${touched.name && errors.name ? 'error' : ''}`}
                      placeholder="Företagsnamn"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                    />
                  </div>
                  {touched.name && errors.name && (
                    <div className="validation-message text-danger">
                      <AlertCircle size={12} className="me-1" />
                      {errors.name}
                    </div>
                  )}
                </div>

                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label style={{ fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                        Org-nummer *
                      </label>
                      <div className="field-wrapper">
                        <Hash className="field-icon" size={16} />
                        <input
                          type="text"
                          className={`modern-input w-100 ${touched.orgnumber && errors.orgnumber ? 'error' : ''}`}
                          placeholder="XXXXXX-XXXX"
                          name="orgnumber"
                          value={values.orgnumber}
                          onChange={handleChange}
                        />
                      </div>
                      {touched.orgnumber && errors.orgnumber && (
                        <div className="validation-message text-danger">
                          <AlertCircle size={12} className="me-1" />
                          {errors.orgnumber}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label style={{ fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                        VAT-nummer
                      </label>
                      <div className="field-wrapper">
                        <Globe className="field-icon" size={16} />
                        <input
                          type="text"
                          className={`modern-input w-100 ${touched.vatnumber && errors.vatnumber ? 'error' : ''}`}
                          placeholder="SEXXXXXXXXXX01"
                          name="vatnumber"
                          value={values.vatnumber}
                          onChange={handleChange}
                        />
                      </div>
                      {touched.vatnumber && errors.vatnumber && (
                        <div className="validation-message text-danger">
                          <AlertCircle size={12} className="me-1" />
                          {errors.vatnumber}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Address Information Section */}
              <div className="mb-4">
                <div className="section-header">
                  <div className="section-icon">
                    <MapPin size={14} />
                  </div>
                  <span className="section-title">Adressinformation</span>
                </div>

                <div className="mb-3">
                  <label style={{ fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                    Adress *
                  </label>
                  <div className="field-wrapper">
                    <MapPin className="field-icon" size={16} />
                    <input
                      type="text"
                      className={`modern-input w-100 ${touched.address && errors.address ? 'error' : ''}`}
                      placeholder="Gatuadress"
                      name="address"
                      value={values.address}
                      onChange={handleChange}
                    />
                  </div>
                  {touched.address && errors.address && (
                    <div className="validation-message text-danger">
                      <AlertCircle size={12} className="me-1" />
                      {errors.address}
                    </div>
                  )}
                </div>

                <Row>
                  <Col md={4}>
                    <div className="mb-3">
                      <label style={{ fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                        Postnummer *
                      </label>
                      <input
                        type="text"
                        className={`modern-input w-100 ${touched.postalcode && errors.postalcode ? 'error' : ''}`}
                        placeholder="123 45"
                        name="postalcode"
                        value={values.postalcode}
                        onChange={handleChange}
                      />
                      {touched.postalcode && errors.postalcode && (
                        <div className="validation-message text-danger">
                          <AlertCircle size={12} className="me-1" />
                          {errors.postalcode}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <label style={{ fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                        Stad *
                      </label>
                      <input
                        type="text"
                        className={`modern-input w-100 ${touched.city && errors.city ? 'error' : ''}`}
                        placeholder="Stockholm"
                        name="city"
                        value={values.city}
                        onChange={handleChange}
                      />
                      {touched.city && errors.city && (
                        <div className="validation-message text-danger">
                          <AlertCircle size={12} className="me-1" />
                          {errors.city}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <label style={{ fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                        Land *
                      </label>
                      <input
                        type="text"
                        className={`modern-input w-100 ${touched.country && errors.country ? 'error' : ''}`}
                        placeholder="Sverige"
                        name="country"
                        value={values.country}
                        onChange={handleChange}
                      />
                      {touched.country && errors.country && (
                        <div className="validation-message text-danger">
                          <AlertCircle size={12} className="me-1" />
                          {errors.country}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Contact Information Section */}
              <div>
                <div className="section-header">
                  <div className="section-icon">
                    <Phone size={14} />
                  </div>
                  <span className="section-title">Kontaktinformation</span>
                </div>

                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label style={{ fontWeight: '500', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                        Telefonnummer
                      </label>
                      <div className="field-wrapper">
                        <Phone className="field-icon" size={16} />
                        <input
                          type="text"
                          className={`modern-input w-100 ${touched.phonenumber && errors.phonenumber ? 'error' : ''}`}
                          placeholder="+46 8 123 456 78"
                          name="phonenumber"
                          value={values.phonenumber}
                          onChange={handleChange}
                        />
                      </div>
                      {touched.phonenumber && errors.phonenumber && (
                        <div className="validation-message text-danger">
                          <AlertCircle size={12} className="me-1" />
                          {errors.phonenumber}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>
            </Modal.Body>

            <Modal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '1.5rem' }}>
              <div className="d-flex justify-content-end gap-3 w-100">
                <Button 
                  variant="outline-secondary" 
                  onClick={handleModalClose}
                  disabled={isSubmitting}
                  style={{ padding: '0.75rem 1.5rem' }}
                >
                  Avbryt
                </Button>
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  style={{ 
                    background: 'var(--bs-primary)', 
                    borderColor: 'var(--bs-primary)',
                    padding: '0.75rem 1.5rem',
                    fontWeight: '600'
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      {initialSupplierData?.supplier_id ? 'Uppdaterar...' : 'Sparar...'}
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} className="me-2" />
                      {initialSupplierData?.supplier_id ? 'Uppdatera leverantör' : 'Skapa leverantör'}
                    </>
                  )}
                </Button>
              </div>
            </Modal.Footer>
          </form>
        </FormikProvider>
      </div>
    </Modal>
  );
};

export default NewSupplierModal;